import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MapPin, Phone, Clock, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { passengerApi } from "@/lib/api";
import { GoogleMapView } from "@/components/GoogleMapView";
import { useSocket } from "@/hooks/useSocket";
import { toast } from "sonner";

export default function PassengerTripStatus() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [tripStatus, setTripStatus] = useState("pending");
  const [trip, setTrip] = useState<any>(null);
  const { on, off } = useSocket();

  useEffect(() => {
    if (!tripId) return;

    const fetchTrip = async () => {
      try {
        const response: any = await passengerApi.getRide(tripId);
        if (response.trip) {
          setTrip(response.trip);
          setTripStatus(response.trip.status);
        }
      } catch (error) {
        console.error("Failed to fetch trip", error);
      }
    };

    fetchTrip();

    // Listen for real-time updates
    on("RIDE_ACCEPTED", (data) => {
      if (data.rideId === tripId) {
        setTripStatus("driver_assigned");
        toast.success("Driver accepted your request!");
        fetchTrip(); // Refresh to get driver info
      }
    });

    on("TRIP_STARTED", (data) => {
      if (data.rideId === tripId) {
        setTripStatus("started");
        toast.info("Trip has started!");
      }
    });

    on("TRIP_COMPLETED", (data) => {
      if (data.rideId === tripId) {
        setTripStatus("completed");
        toast.success("You've arrived at your destination!");
        if (data.tripCode) {
          setTrip((prev: any) => ({ ...prev, trip_code: data.tripCode }));
        }
      }
    });

    on("DRIVER_EN_ROUTE", (data) => {
      if (data.rideId === tripId) {
        setTripStatus("en_route");
      }
    });

    on("DRIVER_ARRIVED", (data) => {
      if (data.rideId === tripId) {
        setTripStatus("arrived");
        toast.info("Driver has arrived!");
      }
    });

    on("TRIP_CANCELLED", (data) => {
      if (data.rideId === tripId) {
        setTripStatus("cancelled");
        toast.error("Trip was cancelled.");
        navigate("/passenger/home");
      }
    });

    return () => {
      off("RIDE_ACCEPTED");
      off("TRIP_STARTED");
      off("TRIP_COMPLETED");
      off("DRIVER_EN_ROUTE");
      off("DRIVER_ARRIVED");
      off("TRIP_CANCELLED");
    };
  }, [tripId]);

  const handleCancelTrip = async () => {
    if (!tripId) return;
    try {
      await passengerApi.cancelRide(tripId);
      toast.success("Trip cancelled successfully");
      navigate("/passenger/home");
    } catch (error) {
      toast.error("Failed to cancel trip");
      console.error(error);
    }
  };

  const statusSteps = [
    { key: "pending", label: "Waiting for Driver", icon: "🔄" },
    { key: "driver_assigned", label: "Driver Assigned", icon: "✓" },
    { key: "en_route", label: "En Route to Pickup", icon: "🚗" },
    { key: "arrived", label: "Driver Arrived", icon: "📍" },
    { key: "started", label: "Trip Started", icon: "🚦" },
    { key: "completed", label: "Trip Completed", icon: "✓" },
  ];

  const currentStepIndex = statusSteps.findIndex((s) => s.key === tripStatus);

  return (
    <div className="min-h-screen bg-yenko-bg pb-32">
      <div className="max-w-2xl mx-auto p-4">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-yenko-deep mb-2">
            Trip #{tripId?.slice(0, 8)}
          </h1>
          <p className="text-yenko-muted">
            Trip Code:{" "}
            <strong className="text-yenko-deep font-mono">
              {trip?.trip_code || "Waiting..."}
            </strong>
          </p>
        </div>

        {/* Live Map View */}
        <div className="h-72 mb-6">
          <GoogleMapView
            pickup={
              trip?.pickup?.lat
                ? {
                    lat: trip.pickup.lat,
                    lng: trip.pickup.lng,
                    address: trip.pickup.address,
                  }
                : undefined
            }
            destination={
              trip?.destination?.lat
                ? {
                    lat: trip.destination.lat,
                    lng: trip.destination.lng,
                    address: trip.destination.address,
                  }
                : undefined
            }
          />
        </div>

        {/* Status Timeline */}
        <Card className="p-6 mb-6">
          <h2 className="font-bold text-yenko-deep mb-6">Trip Progress</h2>
          <div className="space-y-4">
            {statusSteps.map((step, index) => (
              <div key={step.key} className="flex items-start gap-4">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    index <= currentStepIndex
                      ? "bg-yenko-blue text-white"
                      : "bg-yenko-muted/20 text-yenko-muted"
                  }`}
                >
                  {step.icon}
                </div>
                <div className="flex-1">
                  <p
                    className={`font-medium ${
                      index === currentStepIndex
                        ? "text-yenko-blue"
                        : "text-yenko-muted"
                    }`}
                  >
                    {step.label}
                  </p>
                  {index === currentStepIndex && (
                    <p className="text-sm text-yenko-muted">
                      8 minutes remaining
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Driver Info */}
        {trip?.status !== "pending" && trip?.driver_info && (
          <Card className="p-6 mb-6">
            <h2 className="font-bold text-yenko-deep mb-4">Driver Details</h2>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="font-medium text-yenko-deep">
                  {trip.driver_info.name || "Your Driver"}
                </p>
                <p className="text-sm text-yenko-muted">
                  {trip.driver_info.car} • {trip.driver_info.plate}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1">
                <Phone className="w-4 h-4 mr-2" />
                Call
              </Button>
              <Button variant="outline" className="flex-1">
                💬 Message
              </Button>
            </div>
          </Card>
        )}

        {/* Route Summary */}
        <Card className="p-6 mb-6">
          <div className="space-y-4">
            <div className="flex gap-4">
              <MapPin className="w-5 h-5 text-yenko-blue flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm text-yenko-muted">From</p>
                <p className="font-medium text-yenko-deep">
                  {trip?.pickup?.address || "Loading..."}
                </p>
              </div>
            </div>
            <div className="h-12 border-l-2 border-dashed border-yenko-blue ml-2.5" />
            <div className="flex gap-4">
              <MapPin className="w-5 h-5 text-yenko-blue flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm text-yenko-muted">To</p>
                <p className="font-medium text-yenko-deep">
                  {trip?.destination?.address || "Loading..."}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Safety Tip */}
        <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Your location is being shared with your emergency contact
            </p>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="fixed bottom-4 left-4 right-4 max-w-[calc(100%-2rem)] mx-auto flex gap-3">
          <Button variant="outline" className="flex-1">
            <AlertCircle className="w-4 h-4 mr-2" />
            Report Issue
          </Button>
          {(tripStatus === "pending" || tripStatus === "driver_assigned") && (
            <Button
              onClick={handleCancelTrip}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Cancel Trip
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
